import React from "react";
import { Spinner, Alert, Card, Placeholder } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";

// Enhanced loading spinner with animation
export const LoadingSpinner = ({ message = "Finding your perfect recommendations..." }) => (
  <motion.div 
    className="text-center my-5"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.8 }}
    transition={{ duration: 0.3 }}
  >
    <div className="d-flex flex-column align-items-center">
      <Spinner 
        animation="border" 
        variant="primary" 
        style={{ width: '3rem', height: '3rem' }}
        className="mb-3"
      />
      <h5 className="text-muted mb-2">
        <span role="img" aria-label="movie camera">🎬</span> {message}
      </h5>
      <div className="loading-dots">
        <motion.span
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
        >
          •
        </motion.span>
        <motion.span
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
        >
          •
        </motion.span>
        <motion.span
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, delay: 1 }}
        >
          •
        </motion.span>
      </div>
    </div>
  </motion.div>
);

// Skeleton loader for movie cards
export const MovieCardSkeleton = () => (
  <Card className="h-100 cinematic-panel-compact movie-card-cinematic">
    <div className="poster-wrapper">
      <Placeholder as="div" animation="glow" className="w-100 h-100 bg-light" />
    </div>
    <Card.Body className="panel-content-compact">
      <Placeholder as={Card.Title} animation="glow">
        <Placeholder xs={8} />
      </Placeholder>
      <Placeholder as="div" animation="glow" className="mb-2">
        <Placeholder xs={4} size="sm" className="me-2" />
        <Placeholder xs={3} size="sm" />
      </Placeholder>
      <Placeholder as="div" animation="glow" className="mb-3">
        <Placeholder xs={6} size="xs" className="me-1" />
        <Placeholder xs={4} size="xs" />
      </Placeholder>
      <Placeholder as="p" animation="glow">
        <Placeholder xs={12} />
        <Placeholder xs={8} />
        <Placeholder xs={10} />
      </Placeholder>
    </Card.Body>
  </Card>
);

// Enhanced error states
export const ErrorState = ({ 
  title = "Oops! Something went wrong", 
  message = "We encountered an issue while fetching your recommendations.", 
  onRetry = null,
  type = "error" // error, warning, info
}) => {
  const getVariant = () => {
    switch (type) {
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'danger';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'warning': return '⚠️';
      case 'info': return 'ℹ️';
      default: return '😞';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="my-5"
    >
      <Alert variant={getVariant()} className="text-center p-4">
        <div className="mb-3" style={{ fontSize: '3rem' }}>
          {getIcon()}
        </div>
        <Alert.Heading>{title}</Alert.Heading>
        <p className="mb-3">{message}</p>
        {onRetry && (
          <motion.button
            className="btn btn-outline-primary"
            onClick={onRetry}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span role="img" aria-label="refresh">🔄</span> Try Again
          </motion.button>
        )}
      </Alert>
    </motion.div>
  );
};

// Empty state when no results found
export const EmptyState = ({ 
  title = "No movies found", 
  message = "Try adjusting your mood or filters to discover new content.",
  actionText = "Reset Filters",
  onAction = null 
}) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
    className="text-center my-5 py-5"
  >
    <div className="mb-4" style={{ fontSize: '4rem' }}>
      <span role="img" aria-label="theater masks">🎭</span>
    </div>
    <h3 className="mb-3 text-muted">{title}</h3>
    <p className="text-muted mb-4" style={{ maxWidth: '400px', margin: '0 auto' }}>
      {message}
    </p>
    {onAction && (
      <motion.button
        className="btn btn-primary"
        onClick={onAction}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {actionText}
      </motion.button>
    )}
  </motion.div>
);

// Success toast notification
export const SuccessToast = ({ message, show, onClose }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        className="position-fixed top-0 start-50 translate-middle-x mt-3"
        style={{ zIndex: 9999 }}
      >
        <Alert 
          variant="success" 
          dismissible 
          onClose={onClose}
          className="d-flex align-items-center shadow-lg"
        >
          <span role="img" aria-label="checkmark" className="me-2">✅</span>
          {message}
        </Alert>
      </motion.div>
    )}
  </AnimatePresence>
);

// Loading overlay for specific sections
export const LoadingOverlay = ({ show, message = "Loading..." }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
        style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.9)', 
          zIndex: 1000,
          backdropFilter: 'blur(2px)'
        }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-2" />
          <p className="text-muted">{message}</p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);
